/**
 * DemoChat — PLG demo widget.
 *
 * Three states:
 *   1. AUTH       — phone+OTP or Google sign-in (no card required)
 *   2. CHAT       — 5-message streaming conversation with Axia
 *   3. CONVERT    — at message 5, full-screen overlay with activate CTA
 *
 * 2026-05-14 redesign — looks like a modern LLM chat (GPT/Claude/Gemini)
 *   • Flowing assistant text (no bubble) with subtle avatar on the left
 *   • Soft user pill on the right (no aggressive gradient)
 *   • Blinking caret while streaming
 *   • Auto-growing composer textarea
 *   • Empty-state starter prompts users can click
 *   • Inline markdown rendering (bold / italic / code / lists / fences)
 *   • Smooth scroll-to-bottom only when user was already near bottom
 *
 * Endpoints used (orchestrator):
 *   POST /api/demo/initiate         (phone)
 *   POST /api/demo/verify           (otp)
 *   POST /api/demo/google_signin    (google id_token)
 *   POST /api/demo/upload           (multipart, optional)
 *   POST /api/demo/message          (SSE)
 *   POST /api/demo/convert          (final activate)
 *
 * Set `REACT_APP_ORCHESTRATOR_URL` in `.env`.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { streamDemoMessage, friendlyError } from "./sseClient.js";

// 2026-05-15 — Default to axia.equidei.com directly. The previous
// chat.api.equidei.com hostname is a rerouter (python-super-bulma)
// whose nginx doesn't forward the /api/demo/* paths. axia-kaya-v1's
// nginx already proxies everything we need. If your build trigger
// sets REACT_APP_ORCHESTRATOR_URL, that wins.
const ORCH = (
  process.env.REACT_APP_ORCHESTRATOR_URL ||
  "https://axia.equidei.com"
).replace(/\/$/, "");

const GOOGLE_CLIENT_ID =
  process.env.REACT_APP_GOOGLE_CLIENT_ID ||
  "215321644651-0nhjksvfg9g6p7hv9rb8e3o7b5ehc1of.apps.googleusercontent.com";

// Starter prompts — chosen to work COLD (no uploaded files, no
// ERP data yet). Axia answers these from public + intel sources
// alone, so the demo doesn't fail with "I don't have your data".
// 2026-05-15 — replaced the dispatch/SKU/supplier prompts (which
// need user ERP files) with market-intel + benchmark questions
// that work out of the box on a fresh number.
const STARTER_PROMPTS = [
  "Can you analyze my ERP ledgers and raw logs to find price variances and unbilled shipments?",
  "How do WhatsApp Automated Schedules work for weekly OEE digests and daily dispatch reminders?",
];

// Fallback message when demo chat gets initialized without pre-seeds
const INITIAL_DEMO_MESSAGE = {
  role: "assistant",
  text: "Hello! I'm Axia. I can analyze your ERP data, summarize logs, or give you WhatsApp updates. Try clicking a prompt or typing a question below!"
};

const PRE_SEEDED_MESSAGES = [
  {
    role: "user",
    text: "Can you analyze my ERP ledgers and raw logs to find price variances and unbilled shipments?",
  },
  {
    role: "assistant",
    text: `## 📊 DuckDB ERP Analysis: Price Variances & Unbilled Shipments

Using the high-performance **DuckDB Spreadsheet Engine**, I have successfully joined your **ERP purchase ledgers** (\`erp_purchase_ledger_q1.xlsx\`) and your **raw logistics gate logs** (\`gate_entry_logs_q1.csv\`) on purchase order numbers.

Here are the critical discrepancies isolated:

### ⚠️ High-Risk Price Variances (Over-Billing)
We identified **3 orders** where the invoiced rate exceeded the PO contract rate, resulting in a potential overpayment of **₹2,45,000**:

| PO Number | Material | PO Contract Rate | Invoiced Rate | Excess Paid | Status |
| :--- | :--- | :--- | :--- | :--- | :--- |
| **PO-2026-089** | Steel HRC (IS 2062) | ₹54,500/MT | ₹56,200/MT | ₹1,70,000 | 🛑 Overbilled |
| **PO-2026-112** | Aluminum Ingots | ₹2,10,000/MT | ₹2,15,000/MT | ₹50,000 | 🛑 Overbilled |
| **PO-2026-145** | Copper Wire Rods | ₹7,80,000/MT | ₹7,82,500/MT | ₹25,000 | 🛑 Overbilled |

### 🚚 Unbilled Shipments (Leakage Risk)
I isolated **2 gate entries** where material was physically received at your factory warehouse, but no supplier invoice has been logged in your ERP ledger yet:

1. **Gate Entry #GE-8902** (12-Apr-2026): **15.4 MT** of Scrap Plate received from *Shree Balaji Steels*. Estimated value: **₹6,16,000**.
2. **Gate Entry #GE-9145** (28-Apr-2026): **8.2 MT** of CR Sheets received from *National Traders*. Estimated value: **₹4,92,000**.

> [!TIP]
> **Action Item**: Contact Shree Balaji Steels immediately to check for unrecorded invoices before monthly GST ITC reconciliation closes on the 11th.`,
  },
  {
    role: "user",
    text: "How do WhatsApp Automated Schedules work for weekly OEE digests and daily dispatch reminders?",
  },
  {
    role: "assistant",
    text: `## 📱 WhatsApp Automated Schedules & Notification Workflows

Axia integrates directly with the **Meta WhatsApp Business API Gateway** to broadcast scheduled alerts, production digests, and critical reminders directly to you and your shop floor operators.

### 📅 Automated Weekly OEE Production Digest
Every Monday at **08:00 AM**, Axia pulls real-time OEE metrics from your shop-floor database and pushes an executive summary to the management team:

\`\`\`text
[Axia MSME Bot] 📊 WEEKLY FACTORY OEE REPORT
---------------------------------------------
Period: May 12 - May 18, 2026
Overall Plant OEE: 76.5% (↗ +2.1% vs last week)

• Press Line 1: OEE 82.1% [HEALTHY]
• CNC Shop B:   OEE 68.4% [SUB-OPTIMAL]
  - Major downtime: 4.2 hrs (Lubrication fault on CNC #03)
• Assembly 2:   OEE 79.0% [HEALTHY]

💡 Recommendations:
Autonomous maintenance (AM) logs on CNC Shop B are overdue by 3 days. Reminded Supervisor Suresh.
---------------------------------------------
\`\`\`

### 🔔 Daily Dispatch & Payment Reminders
Every evening at **05:00 PM**, Axia reviews outstanding receivables and pending dispatches, triggering automated WhatsApp alerts using pre-approved templates:

1. **To Logistics Team**: *"Hi Vijay, 12.5 MT of Grade IS 2062 HR Plates are scheduled for dispatch tomorrow to Tata Motors. Ensure vehicle MH-43-Y-8902 is cleared by 10 AM."*
2. **To Outstanding Buyers**: *"Dear Accounts, this is a friendly reminder that invoice INV-2026-902 (₹4,50,000) is due for payment on 22-May-2026. Please process via TReDS."*

> [!NOTE]
> These notifications are fully interactive. Operators can respond with commands like \`/delay\` or \`/approve\` to update status instantly.`,
  }
];

// Tiny, dependency-free markdown renderer. Handles the slice modern LLM
// chats actually use: **bold**, *italic*, `code`, ```fenced```, lists,
// and line breaks. Skips images, links, tables — they don't come up in
// 5-message demos and adding them adds 200 KB of deps.
function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const out = [];
  let fence = null; // { lang, lines }
  let listKind = null; // 'ul' | 'ol' | null
  let listItems = [];

  function flushList() {
    if (!listKind) return;
    const Tag = listKind;
    out.push(
      <Tag key={`l${out.length}`} className={listKind === "ul" ? "ml-4 list-disc space-y-1" : "ml-4 list-decimal space-y-1"}>
        {listItems.map((li, i) => (
          <li key={i}>{renderInline(li)}</li>
        ))}
      </Tag>
    );
    listKind = null; listItems = [];
  }

  function renderInline(s) {
    // Process **bold**, *italic*, `code` — keep it linear; no nesting tricks.
    const parts = [];
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*|`[^`]+`)/g;
    let last = 0; let m;
    let key = 0;
    while ((m = regex.exec(s)) !== null) {
      if (m.index > last) parts.push(s.slice(last, m.index));
      const tok = m[0];
      if (tok.startsWith("**")) {
        parts.push(<strong key={key++} className="font-semibold text-white">{tok.slice(2, -2)}</strong>);
      } else if (tok.startsWith("*")) {
        parts.push(<em key={key++} className="italic">{tok.slice(1, -1)}</em>);
      } else {
        parts.push(<code key={key++} className="rounded bg-neutral-800 px-1 py-0.5 font-mono text-[12px] text-fuchsia-200">{tok.slice(1, -1)}</code>);
      }
      last = m.index + tok.length;
    }
    if (last < s.length) parts.push(s.slice(last));
    return parts;
  }

  for (let i = 0; i < lines.length; i++) {
    const ln = lines[i];
    // Fenced code blocks
    if (/^```/.test(ln)) {
      if (fence) {
        out.push(
          <pre key={`p${out.length}`} className="my-2 overflow-x-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 font-mono text-[12px] leading-relaxed text-neutral-200">
            <code>{fence.lines.join("\n")}</code>
          </pre>
        );
        fence = null;
      } else {
        flushList();
        fence = { lang: ln.slice(3).trim(), lines: [] };
      }
      continue;
    }
    if (fence) { fence.lines.push(ln); continue; }

    // Lists
    const ulMatch = ln.match(/^[\s]*[-*]\s+(.*)$/);
    const olMatch = ln.match(/^[\s]*(\d+)\.\s+(.*)$/);
    if (ulMatch) {
      if (listKind !== "ul") { flushList(); listKind = "ul"; }
      listItems.push(ulMatch[1]); continue;
    }
    if (olMatch) {
      if (listKind !== "ol") { flushList(); listKind = "ol"; }
      listItems.push(olMatch[2]); continue;
    }
    flushList();

    // Headings (## and ###)
    if (/^###\s+/.test(ln)) { out.push(<h4 key={out.length} className="mt-3 text-sm font-semibold text-white">{renderInline(ln.replace(/^###\s+/, ""))}</h4>); continue; }
    if (/^##\s+/.test(ln))  { out.push(<h3 key={out.length} className="mt-3 text-base font-semibold text-white">{renderInline(ln.replace(/^##\s+/, ""))}</h3>); continue; }

    // Blank line → paragraph break
    if (!ln.trim()) { out.push(<div key={out.length} className="h-1.5" />); continue; }

    out.push(<p key={out.length} className="leading-relaxed">{renderInline(ln)}</p>);
  }
  flushList();
  if (fence) {
    out.push(<pre key={`p${out.length}`} className="my-2 overflow-x-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 font-mono text-[12px] text-neutral-200"><code>{fence.lines.join("\n")}</code></pre>);
  }
  return out;
}

export default function DemoChat() {
  const [session, setSession] = useState(() => {
    const saved = localStorage.getItem("axia_demo_session");
    return saved ? JSON.parse(saved) : null;
  });

  const [stage, setStage]   = useState(() => {
    const saved = localStorage.getItem("axia_demo_session");
    if (saved) {
      const s = JSON.parse(saved);
      if (s.used >= s.limit) return "convert";
      return "chat";
    }
    return "auth";
  });

  const [phone, setPhone]   = useState("");
  const [otp, setOtp]       = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState(null);
  // 2026-05-19 — OTP resend cooldown. Once an OTP is sent, the user
  // must wait 30 s before requesting another (rate-limits abuse +
  // matches Meta's WhatsApp template throttle). Ticks down to 0 via
  // setInterval; while > 0 the resend button is disabled.
  const [otpResendIn, setOtpResendIn] = useState(0);
  useEffect(() => {
    if (otpResendIn <= 0) return;
    const t = setInterval(() => setOtpResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [otpResendIn]);

  const [messages, setMessages] = useState(() => {
    const saved = localStorage.getItem("axia_demo_session");
    if (saved) {
      const msgs = localStorage.getItem("axia_demo_messages");
      const parsed = msgs ? JSON.parse(msgs) : null;
      return (parsed && parsed.length > 0) ? parsed : [INITIAL_DEMO_MESSAGE];
    }
    return [INITIAL_DEMO_MESSAGE];
  });

  const [input, setInput]   = useState("");
  const [streaming, setStreaming] = useState(false);
  const [uploaded, setUploaded] = useState([]);

  // Synchronize session and messages to localStorage
  useEffect(() => {
    if (session) {
      localStorage.setItem("axia_demo_session", JSON.stringify(session));
    } else {
      localStorage.removeItem("axia_demo_session");
    }
  }, [session]);

  useEffect(() => {
    if (session?.token) {
      localStorage.setItem("axia_demo_messages", JSON.stringify(messages));
    }
  }, [messages, session?.token]);

  const scrollerRef = useRef(null);
  const taRef       = useRef(null);
  const nearBottomRef = useRef(true);
  // 2026-05-15 — Holds a starter prompt the user selected pre-auth.
  // After auth flips stage→chat and session lands, an effect picks
  // this up and fires the message automatically. Avoids the
  // closure-over-stale-session problem if we tried to `send()` from
  // within verifyOtp / googleSignin synchronously.
  const autoFireRef = useRef(null);

  // Inject Google Identity Services once.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    if (document.getElementById("google-gsi")) return;
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true; s.defer = true; s.id = "google-gsi";
    document.body.appendChild(s);
  }, []);

  // Smart auto-scroll: only follow tokens if user was already near the
  // bottom of the chat. If they scrolled up to re-read, leave them alone.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    function onScroll() {
      const gap = el.scrollHeight - el.scrollTop - el.clientHeight;
      nearBottomRef.current = gap < 80;
    }
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [stage]);

  useEffect(() => {
    if (!nearBottomRef.current) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  // Auto-grow the composer textarea (1-6 lines).
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    const max = 6 * 24 + 16; // 6 lines @ 24px line-height + padding
    ta.style.height = Math.min(ta.scrollHeight, max) + "px";
  }, [input, stage]);

  // Pick up a pre-auth starter prompt the moment we land in chat
  // stage with a session in place. Runs once per auth handoff.
  useEffect(() => {
    if (stage !== "chat" || !session?.token) return;
    const pending = autoFireRef.current;
    if (!pending || pending.session?.token !== session.token) return;
    autoFireRef.current = null;
    send(pending.text);
    // CRA's eslint here doesn't have the react-hooks plugin loaded,
    // so suppressing exhaustive-deps by rule name dies the build.
    // `send` is intentionally not in deps; generic suppression below.
    // eslint-disable-next-line
  }, [stage, session?.token]);

  // ── AUTH ────────────────────────────────────────────────────────
  async function sendOtp() {
    setError(null);
    if ((phone.replace(/\D/g, "").length) < 10) {
      setError("Enter a 10-digit Indian mobile number.");
      return;
    }
    setBusy(true);
    try {
      const r = await fetch(`${ORCH}/api/demo/initiate`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || friendlyError(r.status));
      setOtpSent(true);
      // 2026-05-19 — Start the 30-second resend cooldown.
      setOtpResendIn(30);
    } catch (e) {
      setError(e.message || "Could not send OTP. Try again.");
    } finally { setBusy(false); }
  }

  async function verifyOtp() {
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${ORCH}/api/demo/verify`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, otp }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || friendlyError(r.status));
      // Clear previous local storage for a fresh run
      localStorage.removeItem("axia_demo_session");
      localStorage.removeItem("axia_demo_messages");

      const newSession = { token: j.session_token, limit: j.messages_limit || 5, used: j.messages_used || 0 };
      setSession(newSession);
      setStage("chat");
      setMessages([INITIAL_DEMO_MESSAGE]);
      // 2026-05-15 — if the user pre-selected a starter prompt on the
      // auth screen, auto-fire it now so they don't have to click send
      // a second time. Wait one tick for the chat surface to mount.
      const pending = input.trim();
      if (pending) {
        setInput("");
        setTimeout(() => {
          // session state isn't readable yet in this closure, so call
          // send() with an explicit override; sendInternal will pick
          // up the freshly-set session from React state.
          autoFireRef.current = { text: pending, session: newSession };
        }, 0);
      }
    } catch (e) {
      setError(e.message || "OTP verification failed.");
    } finally { setBusy(false); }
  }

  async function googleSignin(idToken) {
    setError(null);
    setBusy(true);
    try {
      const r = await fetch(`${ORCH}/api/demo/google_signin`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id_token: idToken }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || friendlyError(r.status));
      // Clear previous local storage for a fresh run
      localStorage.removeItem("axia_demo_session");
      localStorage.removeItem("axia_demo_messages");

      const newSession = { token: j.session_token, limit: j.messages_limit || 5, used: j.messages_used || 0 };
      setSession(newSession);
      setStage("chat");
      setMessages([INITIAL_DEMO_MESSAGE]);
      const pending = input.trim();
      if (pending) {
        setInput("");
        setTimeout(() => {
          autoFireRef.current = { text: pending, session: newSession };
        }, 0);
      }
    } catch (e) {
      setError(e.message || "Google sign-in failed.");
    } finally { setBusy(false); }
  }

  useEffect(() => {
    function tryRender() {
      if (!window.google?.accounts?.id || !GOOGLE_CLIENT_ID) return;
      const el = document.getElementById("google-btn-slot");
      if (!el) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp) => googleSignin(resp.credential),
      });
      window.google.accounts.id.renderButton(el, {
        type: "standard", theme: "outline", text: "continue_with",
        shape: "rectangular", width: "320",
      });
    }
    const t = setInterval(() => {
      if (window.google?.accounts?.id) { tryRender(); clearInterval(t); }
    }, 200);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [stage]);

  // ── CHAT ────────────────────────────────────────────────────────
  async function handleFiles(list) {
    if (!session?.token || !list?.length) return;
    setError(null);
    const fd = new FormData();
    fd.append("session_token", session.token);
    for (const f of list) {
      if (f.size > 100 * 1024 * 1024) {
        setError(`${f.name} is bigger than 100 MB. Please pick a smaller file.`);
        continue;
      }
      fd.append("files", f, f.name);
    }
    try {
      const r = await fetch(`${ORCH}/api/demo/upload`, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok) throw new Error(j?.detail || friendlyError(r.status));
      setUploaded((u) => [...u, ...(j.accepted || [])]);
      if ((j.skipped || []).length) {
        setError(j.skipped.map((s) => `${s.name}: ${s.reason}`).join(" · "));
      }
    } catch (e) {
      setError(e.message || "Upload failed.");
    }
  }

  async function send(textOverride) {
    const text = (textOverride ?? input).trim();
    if (!session?.token || !text || streaming) return;
    setError(null);
    nearBottomRef.current = true;
    const userMsg = { role: "user", text };
    setMessages((m) => [...m, userMsg, { role: "assistant", text: "" }]);
    setInput("");
    setStreaming(true);
    let assistantText = "";
    try {
      await streamDemoMessage({
        base: ORCH, sessionToken: session.token, query: userMsg.text,
        handlers: {
          onToken: (d) => {
            assistantText += d.text || "";
            // Strip out the redundant conversion pitch and API path sent by backend 
            // since the widget renders a full-page conversion overlay anyway.
            let display = assistantText.replace(/Your 5-message demo is complete[\s\S]*?\[ACTIVATE NOW ->\]\(\/api\/demo\/convert\)-?/g, "");
            display = display.replace(/\[ACTIVATE NOW ->\]\(\/api\/demo\/convert\)/g, "");
            
            setMessages((m) => {
              const next = m.slice();
              next[next.length - 1] = { ...next[next.length - 1], text: display.trim() };
              return next;
            });
          },
          onThinking: (d) => {
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              last.thinkingText = (last.thinkingText || "") + (d.text || "");
              return next;
            });
          },
          // 2026-06-06 — demo now renders code + tool-output widgets too (the
          // backend already streams them; only the rendering was missing).
          onCode: (d) => {
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, codeBlocks: [...(last.codeBlocks || []), { code: d.code || "" }] };
              return next;
            });
          },
          onCodeOut: (d) => {
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              if (!last.codeBlocks?.length) return next;
              const blocks = last.codeBlocks.slice();
              blocks[blocks.length - 1] = { ...blocks[blocks.length - 1], output: (blocks[blocks.length - 1].output || "") + (d.output || "") };
              next[next.length - 1] = { ...last, codeBlocks: blocks };
              return next;
            });
          },
          onWidgetCandidate: (d) => {
            setMessages((m) => {
              const next = m.slice();
              const last = next[next.length - 1];
              next[next.length - 1] = { ...last, widgetCandidates: [...(last.widgetCandidates || []), { kind: d.kind, title: d.title, config: d.config, rationale: d.rationale }] };
              return next;
            });
          },
          onError: (d) => {
            setError(d.message || friendlyError(d.status));
            setMessages((m) => m.slice(0, -1));
            if (d.status === 402 || d.status === 410) setStage("convert");
          },
          onDone: () => {
            const newUsed = (session.used || 0) + 1;
            setSession((s) => ({ ...s, used: newUsed }));
            if (newUsed >= (session.limit || 5)) {
              setTimeout(() => setStage("convert"), 800);
            }
          },
        },
      });
    } catch (e) {
      setError(e.message || "Streaming failed.");
    } finally {
      setStreaming(false);
    }
  }

  // ── CONVERT ─────────────────────────────────────────────────────
  function activate() {
    const url = `https://msme.equidei.com/activate?demo=${encodeURIComponent(session?.token || "")}`;
    window.location.href = url;
  }

  const used  = session?.used  || 0;
  const limit = session?.limit || 5;
  const remaining = Math.max(0, limit - used);

  // ── RENDER ──────────────────────────────────────────────────────
  // 2026-05-15 — Premium layout. Wider (max-w-4xl), purple/fuchsia
  // gradient glow on the border, larger headline + chip text, brighter
  // contrast so the panel reads as the page's hero rather than a
  // black dull box. Wraps with a gradient halo div so the boundary
  // visibly belongs to the EquiDEI palette.
  return (
    <div className="relative mx-auto w-full max-w-4xl">
      {/* Gradient halo behind the panel — purple aura that ties the
          widget to the brand palette and makes it feel premium. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -inset-2 rounded-3xl bg-gradient-to-r from-fuchsia-500/40 via-purple-600/30 to-indigo-600/40 blur-2xl opacity-70"
      />
      {/* Outer surface */}
      <div className="relative flex min-h-[680px] flex-col overflow-hidden rounded-3xl border border-fuchsia-500/30 bg-gradient-to-b from-[#0e0a1a] via-[#0c0e16] to-[#0a0d18] text-neutral-100 shadow-[0_25px_80px_-15px_rgba(165,48,208,0.45)] backdrop-blur">
        {/* 2026-05-15 — Header only shows on the CHAT stage so the
            pre-auth surface doesn't look like a half-rendered chat
            widget. Keeps the page hero-focused until the user actually
            commits to a question. */}
        {(stage === "auth" || stage === "chat") && (
          <>
            <div ref={scrollerRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-6 [&>*+*]:mt-5">
              {/* Empty state — only shows before the first user turn in chat mode */}
              {stage === "chat" && messages.filter((m) => m.role === "user").length === 0 && (
                <div className="flex flex-col items-center gap-5 py-8 text-center">
                  <div className="space-y-1">
                    <div className="text-lg font-semibold tracking-tight">How can I help today?</div>
                    <p className="text-xs text-neutral-400">
                      Try one of these — or just type below.
                    </p>
                  </div>
                  <div className="grid w-full max-w-2xl grid-cols-1 gap-2 sm:grid-cols-2">
                    {STARTER_PROMPTS.map((p) => (
                      <button
                        key={p} type="button" onClick={() => send(p)}
                        className="rounded-xl border border-neutral-800 bg-neutral-900/60 px-3 py-2.5 text-left text-xs text-neutral-300 transition hover:border-fuchsia-500/50 hover:bg-neutral-900 hover:text-white"
                      >
                        {p}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Conversation */}
              {messages.map((m, i) => {
                const isUser    = m.role === "user";
                const isLast    = i === messages.length - 1;
                const isCaret   = !isUser && isLast && streaming;
                return (
                  <div key={i} className={isUser ? "flex justify-end" : "flex gap-3"}>
                    {!isUser && (
                      <div className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-gradient-to-br from-fuchsia-500 to-indigo-600" />
                    )}
                    {isUser ? (
                      <div className="max-w-[80%] whitespace-pre-wrap rounded-2xl bg-neutral-800/80 px-4 py-2.5 text-sm leading-relaxed text-neutral-100">
                        {m.text}
                      </div>
                    ) : (
                      <div className="flex-1 space-y-1 text-sm text-neutral-200">
                        {m.thinkingText && (
                          <details className="mb-2 text-xs text-neutral-400 marker:text-neutral-500" open>
                            <summary className="cursor-pointer select-none">Thought process</summary>
                            <div className="mt-1 whitespace-pre-wrap rounded bg-neutral-900/50 p-2 border border-neutral-800">
                              {m.thinkingText}
                            </div>
                          </details>
                        )}
                        {Array.isArray(m.codeBlocks) && m.codeBlocks.map((b, ci) => (
                          <details key={ci} className="mb-2 text-xs" open>
                            <summary className="cursor-pointer select-none text-neutral-400">Code</summary>
                            <pre className="mt-1 overflow-x-auto rounded border border-neutral-800 bg-neutral-900/70 p-2 text-[11px] text-emerald-300"><code>{b.code}</code></pre>
                            {b.output && (
                              <pre className="mt-1 overflow-x-auto rounded border border-neutral-800 bg-neutral-900/40 p-2 text-[11px] text-neutral-300"><code>{b.output}</code></pre>
                            )}
                          </details>
                        ))}
                        {Array.isArray(m.widgetCandidates) && m.widgetCandidates.length > 0 && (
                          <div className="mb-2 grid gap-2 sm:grid-cols-2">
                            {m.widgetCandidates.map((w, wi) => {
                              const cfg = w.config || {};
                              const rows = Array.isArray(cfg.rows) ? cfg.rows : null;
                              return (
                                <div key={wi} className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
                                  <div className="flex items-center justify-between">
                                    <div className="text-xs font-medium text-neutral-200">{w.title || w.kind}</div>
                                    <span className="rounded-full bg-fuchsia-500/15 px-2 py-0.5 text-[9px] uppercase tracking-wide text-fuchsia-300">{String(w.kind || "").replace("_", " ")}</span>
                                  </div>
                                  {w.kind === "kpi" ? (
                                    <div className="mt-1 text-2xl font-bold text-white">
                                      {String(cfg.value ?? cfg.metric ?? "—")}
                                      {cfg.unit ? <span className="ml-1 text-sm text-neutral-400">{cfg.unit}</span> : null}
                                    </div>
                                  ) : rows ? (
                                    <div className="mt-1 max-h-32 overflow-auto">
                                      <table className="w-full text-[11px] text-neutral-300">
                                        <tbody>
                                          {rows.slice(0, 6).map((row, ri) => (
                                            <tr key={ri} className="border-b border-neutral-800/60">
                                              {(Array.isArray(row) ? row : Object.values(row)).slice(0, 4).map((cell, cj) => (
                                                <td key={cj} className="py-0.5 pr-2">{String(cell)}</td>
                                              ))}
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  ) : (
                                    <div className="mt-1 text-[11px] text-neutral-400">📊 Live {String(w.kind || "chart").replace("_", " ")} — open in the app for the interactive view.</div>
                                  )}
                                  {w.rationale && <div className="mt-1 text-[10px] text-neutral-500">{w.rationale}</div>}
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {!m.text && isCaret ? (
                          <div className="flex items-center gap-1 py-1.5 text-neutral-400">
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fuchsia-500 [animation-delay:-0.3s]"></span>
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fuchsia-500 [animation-delay:-0.15s]"></span>
                            <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-fuchsia-500"></span>
                          </div>
                        ) : m.text ? (
                          renderMarkdown(m.text)
                        ) : null}
                        {isCaret && (
                          <span className="ml-0.5 inline-block h-[1em] w-[2px] -mb-[2px] animate-pulse bg-fuchsia-400 align-middle" aria-hidden="true" />
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Files chip */}
              {uploaded.length > 0 && (
                <div className="text-[10px] text-neutral-500">
                  Attached: {uploaded.map((u) => u.name).join(" · ")}
                </div>
              )}
            </div>

            {error && stage === "chat" && (
              <div className="border-t border-red-900/50 bg-red-900/15 px-4 py-1.5 text-[11px] text-red-300">
                {error}
              </div>
            )}

            {/* Composer - only on CHAT stage */}
            {stage === "chat" && (
              <div className="border-t border-neutral-800/80 bg-[#0c0e16]/90 px-3 py-3 backdrop-blur">
                <div className="flex items-end gap-2 rounded-2xl border border-neutral-800 bg-neutral-900 px-2.5 py-2 focus-within:border-fuchsia-500/60">
                  <label className="cursor-pointer rounded-lg p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100" title="Attach Excel / PDF (≤100 MB)">
                    <input
                      type="file" multiple className="hidden"
                      accept=".xlsx,.xls,.csv,.pdf,.png,.jpg,.jpeg,.docx"
                      onChange={(e) => { handleFiles(e.target.files); e.target.value = ""; }}
                    />
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 17.93 8.8l-8.58 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                  </label>
                  <textarea
                    ref={taRef} rows={1} value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder={streaming ? "Axia is replying…" : "Ask Axia anything about your factory…"}
                    className="max-h-40 min-h-[24px] flex-1 resize-none bg-transparent px-1 py-1 text-sm leading-6 placeholder:text-neutral-500 focus:outline-none"
                  />
                  <button
                    type="button" onClick={() => send()}
                    disabled={streaming || !input.trim()}
                    aria-label="Send"
                    className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-600 text-white transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/></svg>
                  </button>
                </div>
                <div className="mt-1.5 px-1 text-[10px] text-neutral-500 flex justify-between">
                  <span>Press Enter to send · Shift+Enter for a new line</span>
                  <span>{remaining}/5 free messages left</span>
                </div>
              </div>
            )}

            {/* Premium CTA Inline Authentication Block - shown only on AUTH stage at the bottom of the preloaded tour */}
            {stage === "auth" && (
              <div className="border-t border-neutral-800/80 bg-[#0c0e16]/95 px-6 py-5 backdrop-blur">
                <div className="mx-auto max-w-2xl text-center space-y-4">
                  <div>
                    <div className="inline-flex items-center gap-1.5 rounded-full bg-fuchsia-500/10 px-3 py-1 text-[11px] font-medium text-fuchsia-300 ring-1 ring-inset ring-fuchsia-500/20">
                      ⚡️ Interactive Product Showcase
                    </div>
                    <h3 className="mt-2 text-base font-semibold tracking-tight text-white">
                      Want to Ask Axia About Your Own Factory?
                    </h3>
                    <p className="mt-1 text-xs text-neutral-400">
                      Verify your WhatsApp number or sign in with Google to upload your own Tally ledger, ERP sheets, or raw files.
                    </p>
                  </div>

                  {!otpSent ? (
                    <div className="space-y-3">
                      <div className="mx-auto max-w-md rounded-2xl border border-neutral-800 bg-neutral-900 px-2.5 py-2 focus-within:border-fuchsia-500/60">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                          <div className="flex flex-1 items-center gap-1 min-w-0">
                            <span className="px-1 py-1.5 text-sm text-neutral-400 shrink-0">+91</span>
                            <input
                              type="tel" inputMode="numeric" autoFocus
                              value={phone} onChange={(e) => setPhone(e.target.value)}
                              placeholder="WhatsApp number"
                              className="min-w-0 flex-1 bg-transparent px-1 py-1.5 text-sm placeholder:text-neutral-500 focus:outline-none text-white"
                              onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
                            />
                          </div>
                          <button
                            type="button" onClick={sendOtp} disabled={busy}
                            className="flex h-9 w-full items-center justify-center gap-1 rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-600 px-4 text-xs font-semibold text-white shadow-md shadow-fuchsia-500/20 transition hover:brightness-110 disabled:opacity-40 sm:h-8 sm:w-auto"
                          >
                            {busy ? "Sending…" : "Send OTP on WhatsApp →"}
                          </button>
                        </div>
                      </div>

                      {GOOGLE_CLIENT_ID && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-3">
                            <span className="h-px flex-1 bg-neutral-800" />
                            <span className="text-[9px] uppercase tracking-widest text-neutral-500">or</span>
                            <span className="h-px flex-1 bg-neutral-800" />
                          </div>
                          <div id="google-btn-slot" className="flex justify-center" />
                        </div>
                      )}

                      <div className="text-[10px] text-neutral-500">
                        No credit card required · 5 free custom messages
                      </div>
                    </div>
                  ) : (
                    <div className="mx-auto max-w-md space-y-3">
                      <div className="text-xs text-neutral-400 text-center">
                        Enter the 6-digit code we just sent on WhatsApp to +91 {phone}
                      </div>
                      <div className="flex items-end gap-2 rounded-2xl border border-neutral-800 bg-neutral-900 px-2.5 py-2 focus-within:border-fuchsia-500/60">
                        <input
                          type="tel" inputMode="numeric" maxLength={6} autoFocus
                          value={otp} onChange={(e) => setOtp(e.target.value)}
                          placeholder="• • • • • •"
                          className="flex-1 bg-transparent px-2 py-1.5 text-center font-mono text-base tracking-[0.4em] placeholder:text-neutral-600 focus:outline-none text-white"
                          onKeyDown={(e) => { if (e.key === "Enter" && otp.length >= 6) verifyOtp(); }}
                        />
                        <button
                          type="button" onClick={verifyOtp} disabled={busy || otp.length < 6}
                          className="flex h-8 items-center gap-1 rounded-lg bg-gradient-to-br from-fuchsia-500 to-indigo-600 px-3 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-40"
                        >
                          {busy ? "Verifying…" : "Verify →"}
                        </button>
                      </div>
                      <div className="flex items-center justify-between text-[10px] text-neutral-500 px-1">
                        <button
                          type="button" onClick={() => { setOtpSent(false); setOtp(""); }}
                          className="underline hover:text-neutral-200"
                        >
                          Use a different number
                        </button>
                        <button
                          type="button"
                          onClick={() => { setOtp(""); sendOtp(); }}
                          disabled={otpResendIn > 0 || busy}
                          className="underline hover:text-neutral-200 disabled:no-underline disabled:cursor-not-allowed disabled:text-neutral-600"
                        >
                          {otpResendIn > 0 ? `Resend in ${otpResendIn}s` : "Resend OTP"}
                        </button>
                      </div>
                    </div>
                  )}

                  {error && (
                    <div className="mx-auto max-w-md rounded-md border border-red-900/50 bg-red-900/15 px-3 py-1.5 text-[11px] text-red-300">
                      {error}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {/* CONVERT ────────────────────────────────────────────────── */}
        {stage === "convert" && (
          <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 py-12 text-center">
            <div className="text-3xl">🎉</div>
            <div className="text-lg font-semibold tracking-tight">Your factory intelligence is ready.</div>
            <p className="max-w-md text-sm text-neutral-400">
              Activate Axia to keep this conversation, get daily reports,
              set reminders, and use Axia on WhatsApp.
            </p>
            <div className="text-sm font-semibold text-white">
              ₹2,100/month · cancel anytime
            </div>
            <button
              type="button" onClick={activate}
              className="w-full max-w-xs rounded-xl bg-gradient-to-br from-fuchsia-500 to-indigo-600 py-2.5 text-sm font-medium shadow-lg shadow-fuchsia-500/20 transition hover:brightness-110"
            >
              Activate Axia →
            </button>
            {/* 2026-05-19 — Replaced the single-WhatsApp "Talk to
                Raghav" CTA with two explicit channels (email +
                LinkedIn). LinkedIn URL fixed to the correct handle
                /in/raghavequidei (was /in/raghavgupta-equidei before). */}
            <div className="flex items-center justify-center gap-3 text-xs text-neutral-400">
              <a
                href="mailto:raghav@equidei.com?subject=Axia%20demo%20follow-up"
                className="underline hover:text-neutral-200"
              >
                Email Raghav
              </a>
              <span className="text-neutral-600">·</span>
              <a
                href="https://www.linkedin.com/in/raghavequidei"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-neutral-200"
              >
                LinkedIn
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
